import { useCallback, useMemo } from 'react';

import * as Select from '@radix-ui/react-select';

import { ReactComponent as Chile } from '@quick/assets/countries/chile.svg';
import { ReactComponent as Colombia } from '@quick/assets/countries/colombia.svg';
import { ReactComponent as Mexico } from '@quick/assets/countries/mexico.svg';
import { ReactComponent as USA } from '@quick/assets/countries/usa.svg';
import { cn } from '@quick/utils';

import { useLangProvider } from '@/components/core/AppLayout/AppLangProvider';
import { envs } from '@/layers/core/data/const';
import { useCountry } from '@/layers/core/deal/services/CountryContext';

const data = {
  CO: {
    value: 'CO',
    text: 'Colombia',
    Icon: Colombia,
  },
  CL: {
    value: 'CL',
    text: 'Chile',
    Icon: Chile,
  },
  MX: {
    value: 'MX',
    text: 'Mexico',
    Icon: Mexico,
  },
  US: {
    value: 'US',
    text: 'USA',
    Icon: USA,
  },
};

type ICountryOption = (typeof data)['CO'];

export function CountrySelector({ className }: { className?: string }) {
  const lang = useLangProvider();
  // const { t } = useTranslation(['common', 'dl']);
  const { clean, country } = useCountry();
  const countryList = useMemo(() => {
    const hosts = Object.entries(envs.hosts.urls).reduce(
      (prev, [k, v]) => {
        if (!!v.clean === !!clean)
          prev[v.country] ||= { ...data[v.country], url: k };
        return prev;
      },
      {} as Record<string, ICountryOption & { url: string }>
    );
    return Object.values(hosts);
  }, [clean]);

  const redirect = useCallback(
    (countryID: string | undefined) => {
      if (!countryID) return;
      const country = countryList.find(({ value }) => value === countryID);
      if (!country) return;
      window.location.href = `https://${country.url}${
        lang ? `?lang=${lang}` : ''
      }`;
    },
    [lang, countryList]
  );

  if (countryList.length <= 1) return null;
  return (
    <Select.Root defaultValue={country} onValueChange={redirect}>
      <Select.Trigger
        className={cn(
          `hover:text-brand focus:text-brand cs flex gap-1 text-white hover:outline-none
          focus:outline-none`,
          className
        )}
        aria-label="country"
      >
        <Select.Value className="flex text-red-500" />
        <Select.Icon className="self-center text-xs" />
      </Select.Trigger>

      <Select.Portal>
        <Select.Content className="z-100 overflow-hidden rounded bg-gray-600 text-gray-200">
          <Select.ScrollUpButton></Select.ScrollUpButton>
          <Select.Viewport className="flex flex-col gap-1 p-1">
            {countryList.map(({ Icon, text, url, value }) => (
              <Select.Item value={value} key={value} asChild>
                <a
                  href={`https://${url}`}
                  className="relative flex rounded p-1 pr-5 hover:bg-gray-800 hover:outline-none
                    focus:bg-gray-800 focus:outline-none"
                >
                  <Select.ItemText className="inline-flex">
                    <span className="flex items-center gap-2 font-semibold">
                      <Icon className="inline-block h-4 w-4" />
                      <span className="[.cs_&]:max-md:sr-only">{text}</span>
                    </span>
                  </Select.ItemText>
                  <Select.ItemIndicator>
                    <span className="bg-brand absolute right-1 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full" />
                  </Select.ItemIndicator>
                </a>
              </Select.Item>
            ))}
          </Select.Viewport>
          <Select.SelectScrollDownButton />
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
